import { createEntityComponent } from "@coltorapps/builder-react";
import { Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAtomValue } from "jotai";

import { supportsVisibilityAction } from "@/const/flow";
import TablePagination from "@/components/table/table-pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/ui/modal";
import { Select, type SelectOption } from "@/components/ui/select";
import { formatError, ValidationError } from "@/components/ui/validation-error";
import { useDebounce } from "@/hooks/useDebounce";
import {
  useBpmDatasetRecords,
  useModalSelectionExternalApiOptions,
} from "@/hooks/useMasterData";
import {
  interpreterStoreAtom,
  runtimeApplicationAtom,
  runtimeFormAtom,
  selectedApplicantAtom,
} from "@/store";
import type { Operator } from "@/types/flow";
import { VisibilityAction } from "@/types/flow";
import { EntityKey } from "@/types/form-builder";
import type {
  DatasetFilterExpressionRequest,
  ModalSelectionExternalApiOptionsRequest,
} from "@/types/master-data-dataset";
import { toValidApplicantId } from "@/utils/applicant-id";
import { cn } from "@/utils/cn";
import { parseFormData } from "@/utils/parser";

import type {
  ModalDatasourceExternalColumn,
  ModalDatasourceFilter,
  ModalDatasourceMasterDataColumn,
  ModalDatasourceValue,
} from "../../attributes/modal-datasource/definition";
import { modalSelectionEntity } from "./definition";

type PickerRow = Record<string, unknown>;

type PickerColumn = {
  key: string;
  label: string;
};

type MappedColumn = {
  sourceKey: string;
  targetFieldName: string;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const SERVER_FILTER_OPERATORS: Operator[] = ["==", "equals"];
const EXTERNAL_API_MAX_RECORDS = 100;
const DEFAULT_TIME_ZONE = "UTC";

const emptySelectionState = {
  key: undefined,
  row: undefined,
} as const;

const resolvePath = (source: unknown, rawPath: string): unknown => {
  const path = rawPath.trim();
  if (!path) return source;

  return path
    .split(".")
    .filter(Boolean)
    .reduce<unknown>((acc, segment) => {
      if (acc === null || acc === undefined) return undefined;
      if (typeof acc !== "object") return undefined;
      return (acc as Record<string, unknown>)[segment];
    }, source);
};

const compareWithOperator = (
  operator: Operator | undefined,
  left: unknown,
  right: unknown,
): boolean => {
  if (!operator) return true;

  const leftNum = Number(left);
  const rightNum = Number(right);
  const numeric = Number.isFinite(leftNum) && Number.isFinite(rightNum);

  switch (operator) {
    case ">":
      return numeric ? leftNum > rightNum : String(left) > String(right);
    case "<":
      return numeric ? leftNum < rightNum : String(left) < String(right);
    case ">=":
      return numeric ? leftNum >= rightNum : String(left) >= String(right);
    case "<=":
      return numeric ? leftNum <= rightNum : String(left) <= String(right);
    case "==":
    case "equals":
      return String(left ?? "") === String(right ?? "");
    case "!=":
    case "not_equals":
      return String(left ?? "") !== String(right ?? "");
    case "contains":
      return String(left ?? "").includes(String(right ?? ""));
    case "not_contains":
      return !String(left ?? "").includes(String(right ?? ""));
    default:
      return false;
  }
};

const isRecord = (value: unknown): value is PickerRow =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const safeSerializeRecord = (value: unknown): string | undefined => {
  if (!isRecord(value)) return undefined;

  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
};

const normalizeRows = (value: unknown): PickerRow[] => {
  if (!Array.isArray(value)) return [];

  return value.filter(isRecord);
};

const formatCellValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string") return value;
  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return String(value);
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const getConfiguredColumns = (
  datasource?: ModalDatasourceValue,
): PickerColumn[] => {
  if (!datasource) return [];

  if (datasource.type === "master-data") {
    return datasource.config.columns
      .filter((column) => column.columnKey.trim())
      .map((column: ModalDatasourceMasterDataColumn) => ({
        key: column.columnKey,
        label: column.displayName.trim() || column.columnKey,
      }));
  }

  return datasource.config.field_mappings.mappings
    .filter((column) => column.columnKey.trim())
    .map((column: ModalDatasourceExternalColumn) => ({
      key: column.columnKey,
      label: column.displayName.trim() || column.columnKey,
    }));
};

const getDatasourceFilters = (
  datasource?: ModalDatasourceValue,
): ModalDatasourceFilter[] => {
  if (!datasource) return [];
  if (
    (datasource.config as { filterMode?: "manual" | "code" }).filterMode ===
    "code"
  ) {
    return [];
  }

  return datasource.config.filters;
};

const hasMeaningfulFilterValue = (value: unknown): value is string | number => {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  return true;
};

const getDatasourceFilterExpression = (
  datasource?: ModalDatasourceValue,
): string | undefined => {
  if (!datasource) return undefined;

  const config = datasource.config as {
    filterMode?: "manual" | "code";
    filterExpression?: string | { expression?: string };
  };

  if (config.filterMode !== "code") {
    return undefined;
  }

  const rawFilterExpression = (
    datasource.config as {
      filterExpression?: string | { expression?: string };
    }
  ).filterExpression;

  if (typeof rawFilterExpression === "string") {
    const expression = rawFilterExpression.trim();
    return expression || undefined;
  }

  if (
    rawFilterExpression &&
    typeof rawFilterExpression === "object" &&
    typeof rawFilterExpression.expression === "string"
  ) {
    const expression = rawFilterExpression.expression.trim();
    return expression || undefined;
  }

  return undefined;
};

const applyDatasourceFilters = (
  rows: PickerRow[],
  filters: ModalDatasourceFilter[],
): PickerRow[] => {
  const activeFilters = filters.filter((filter) => filter.columnKey.trim());
  if (activeFilters.length === 0) return rows;

  return rows.filter((row) =>
    activeFilters.every((filter) =>
      compareWithOperator(
        filter.operator,
        resolvePath(row, filter.columnKey),
        filter.value,
      ),
    ),
  );
};

const getMappedColumns = (
  datasource?: ModalDatasourceValue,
): MappedColumn[] => {
  if (!datasource) return [];

  if (datasource.type === "master-data") {
    return datasource.config.columns
      .filter(
        (column) =>
          Boolean(column.columnKey.trim()) && Boolean(column.formField?.trim()),
      )
      .map((column: ModalDatasourceMasterDataColumn) => ({
        sourceKey: column.columnKey,
        targetFieldName: column.formField!.trim(),
      }));
  }

  return datasource.config.field_mappings.mappings
    .filter(
      (column) =>
        Boolean(column.columnKey.trim()) && Boolean(column.formField.trim()),
    )
    .map((column: ModalDatasourceExternalColumn) => ({
      sourceKey: column.columnKey,
      targetFieldName: column.formField.trim(),
    }));
};

const isServerFilterCompatible = (filter: ModalDatasourceFilter): boolean =>
  Boolean(filter.columnKey.trim()) &&
  SERVER_FILTER_OPERATORS.includes(filter.operator);

const getRowSelectionKey = (row: PickerRow): string =>
  safeSerializeRecord(row) ??
  globalThis.crypto?.randomUUID?.() ??
  String(Date.now());

const getBrowserTimeZone = (): string => {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIME_ZONE
    );
  } catch {
    return DEFAULT_TIME_ZONE;
  }
};

const resolveFieldIdByName = (
  fieldName: string,
  iStore: {
    schema?: {
      entities?: Record<
        string,
        { type?: string; attributes?: Record<string, unknown> }
      >;
    };
  } | null,
): string | undefined => {
  if (!iStore) return undefined;

  const entities = iStore.schema?.entities ?? {};

  return (
    Object.entries(entities).find(
      ([, entity]) => entity.attributes?.name === fieldName,
    )?.[0] ??
    (Object.prototype.hasOwnProperty.call(entities, fieldName)
      ? fieldName
      : undefined)
  );
};

const getBooleanAttribute = (
  attributes: Record<string, unknown>,
  key: string,
): boolean | undefined =>
  typeof attributes[key] === "boolean"
    ? (attributes[key] as boolean)
    : undefined;

const coerceDateFieldValue = (value: unknown): unknown => {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? value : parsed;
  }

  return value;
};

const coerceMappedValueForEntity = (
  value: unknown,
  entity?: {
    type?: string;
    attributes?: Record<string, unknown>;
  },
): unknown => {
  const entityType = entity?.type as EntityKey | undefined;

  if (entityType === EntityKey.datePickerField) {
    return coerceDateFieldValue(value);
  }

  return value;
};

const isTargetFieldEditable = (entity?: {
  type?: string;
  attributes?: Record<string, unknown>;
}): boolean => {
  if (!entity) return false;

  const entityType = entity.type as EntityKey | undefined;
  if (!entityType) return false;

  const attributes = entity.attributes ?? {};
  const isHidden =
    getBooleanAttribute(attributes, "hide") === true ||
    getBooleanAttribute(attributes, "hidden") === true;
  const isDisabled = getBooleanAttribute(attributes, "disabled") === true;
  const isReadonly = getBooleanAttribute(attributes, "readonly") === true;

  if (isHidden || isDisabled || isReadonly) {
    return false;
  }

  if (entityType === EntityKey.grid) {
    return supportsVisibilityAction(entityType, VisibilityAction.GRID_EDITABLE);
  }

  return supportsVisibilityAction(entityType, VisibilityAction.EDITABLE);
};

const SelectionIndicator = ({ selected }: { selected: boolean }) => (
  <span
    className={cn(
      "flex h-5 w-5 items-center justify-center rounded-full border transition-colors",
      selected ? "border-giant-blue" : "border-[#C4CDD5]",
    )}
  >
    <span
      className={cn(
        "h-2.5 w-2.5 rounded-full transition-colors",
        selected ? "bg-giant-blue" : "bg-transparent",
      )}
    />
  </span>
);

export const ModalSelectionEntity = createEntityComponent(
  modalSelectionEntity,
  function ModalSelectionEntity(props) {
    const label =
      props.entity.attributes.label.value || props.entity.attributes.name;
    const buttonText = props.entity.attributes.buttonText?.trim()
      ? props.entity.attributes.buttonText
      : "Select";
    const datasource = props.entity.attributes.modalDatasource as
      | ModalDatasourceValue
      | undefined;
    const interpreterStore = useAtomValue(interpreterStoreAtom);
    const selectedApplicant = useAtomValue(selectedApplicantAtom);
    const runtimeApplication = useAtomValue(runtimeApplicationAtom);
    const runtimeForm = useAtomValue(runtimeFormAtom);

    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchColumn, setSearchColumn] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedKey, setSelectedKey] = useState<string | undefined>(
      emptySelectionState.key,
    );
    const [selectedRow, setSelectedRow] = useState<PickerRow | undefined>(
      emptySelectionState.row,
    );
    const [externalRows, setExternalRows] = useState<PickerRow[]>([]);
    const [externalError, setExternalError] = useState("");

    const masterDataConfig =
      datasource?.type === "master-data" ? datasource.config : undefined;
    const externalConfig =
      datasource?.type === "external-api" ? datasource.config : undefined;
    const entityKey = props.entity.attributes.name?.trim()
      ? props.entity.attributes.name.trim()
      : props.entity.id;

    const {
      mutateAsync: loadExternalApiOptions,
      isPending: isTestingExternalApi,
    } = useModalSelectionExternalApiOptions();
    const externalApiRef = useRef(loadExternalApiOptions);
    const externalConfigRef = useRef(externalConfig);
    const userTimeZone = useMemo(() => getBrowserTimeZone(), []);

    useEffect(() => {
      externalApiRef.current = loadExternalApiOptions;
    }, [loadExternalApiOptions]);

    useEffect(() => {
      externalConfigRef.current = externalConfig;
    }, [externalConfig]);

    const columns = useMemo(
      () => getConfiguredColumns(datasource),
      [datasource],
    );
    const mappedColumns = useMemo(
      () => getMappedColumns(datasource),
      [datasource],
    );
    const currentEntityValues = (interpreterStore?.getEntitiesValues?.() ??
      {}) as Record<string, unknown>;
    const datasourceFilterExpression = useMemo(
      () => getDatasourceFilterExpression(datasource),
      [datasource],
    );
    const filterExpressionPayload = useMemo<
      DatasetFilterExpressionRequest | undefined
    >(() => {
      if (!datasourceFilterExpression) return undefined;

      const formData = interpreterStore?.schema
        ? parseFormData(currentEntityValues, interpreterStore.schema).data
        : {};
      const applicantId =
        toValidApplicantId(selectedApplicant?.id) ??
        toValidApplicantId(runtimeApplication?.applicantId) ??
        toValidApplicantId(runtimeApplication?.submitterId) ??
        toValidApplicantId(runtimeApplication?.submittedBy);

      return {
        expression: datasourceFilterExpression,
        form_data: formData,
        ...(applicantId ? { applicantId } : {}),
      };
    }, [
      currentEntityValues,
      datasourceFilterExpression,
      interpreterStore?.schema,
      runtimeApplication?.applicantId,
      runtimeApplication?.submittedBy,
      runtimeApplication?.submitterId,
      selectedApplicant?.id,
    ]);
    const filterExpressionPayloadKey = useMemo(
      () => JSON.stringify(filterExpressionPayload ?? null),
      [filterExpressionPayload],
    );
    const stableFilterExpressionPayload = useMemo(
      () => filterExpressionPayload,
      [filterExpressionPayloadKey],
    );

    const searchOptions = useMemo<SelectOption<string>[]>(
      () =>
        columns.map((column) => ({
          label: column.label,
          value: column.key,
          key: column.key,
        })),
      [columns],
    );

    const datasourceFilters = useMemo(
      () => getDatasourceFilters(datasource),
      [datasource],
    );
    const hasDatasourceFilterExpression = Boolean(
      stableFilterExpressionPayload,
    );
    const debouncedSearchQuery = useDebounce(searchQuery, 300);
    const datasourceFiltersKey = useMemo(
      () => JSON.stringify(datasourceFilters),
      [datasourceFilters],
    );
    const trimmedSearchQuery = debouncedSearchQuery.trim();
    const hasSearchQuery = Boolean(searchColumn && trimmedSearchQuery);
    const hasUnsupportedDatasourceFilters = useMemo(
      () =>
        datasourceFilters.some(
          (filter) =>
            Boolean(filter.columnKey.trim()) &&
            !isServerFilterCompatible(filter),
        ),
      [datasourceFilters],
    );
    const localDatasourceFilters = useMemo(
      () =>
        datasourceFilters.filter(
          (filter) =>
            Boolean(filter.columnKey.trim()) &&
            !isServerFilterCompatible(filter),
        ),
      [datasourceFilters],
    );
    const useServerPagination =
      datasource?.type === "master-data" && !hasUnsupportedDatasourceFilters;

    const requestFilterParams = useMemo(() => {
      if (hasDatasourceFilterExpression) return undefined;

      const params: Record<string, string | number> = {};

      datasourceFilters.forEach((filter) => {
        if (!isServerFilterCompatible(filter)) return;
        const value = filter.value;
        if (!hasMeaningfulFilterValue(value)) return;
        params[filter.columnKey.trim()] =
          typeof value === "string" ? value.trim() : value;
      });
      return Object.keys(params).length > 0 ? params : undefined;
    }, [datasourceFilters, hasDatasourceFilterExpression]);

    const requestSearchParams = useMemo(() => {
      if (!hasSearchQuery || !searchColumn) return undefined;

      return {
        [searchColumn]: trimmedSearchQuery,
      };
    }, [hasSearchQuery, searchColumn, trimmedSearchQuery]);

    const masterDataSelectFields = useMemo(() => {
      const keys = columns.map((column) => column.key.trim()).filter(Boolean);
      return Array.from(new Set(keys));
    }, [columns]);

    const masterDataQueryParams = useMemo(
      () => ({
        page: useServerPagination ? page : 1,
        limit: useServerPagination ? pageSize : 1000,
        select:
          masterDataSelectFields.length > 0
            ? masterDataSelectFields
            : undefined,
        filter:
          useServerPagination && !hasDatasourceFilterExpression
            ? requestFilterParams
            : undefined,
        search: useServerPagination ? requestSearchParams : undefined,
        filterExpression:
          useServerPagination && hasDatasourceFilterExpression
            ? stableFilterExpressionPayload
            : undefined,
      }),
      [
        hasDatasourceFilterExpression,
        masterDataSelectFields,
        page,
        pageSize,
        requestFilterParams,
        requestSearchParams,
        stableFilterExpressionPayload,
        useServerPagination,
      ],
    );

    const {
      records: masterDataRows,
      total: masterDataTotal,
      page: masterDataCurrentPage,
      limit: masterDataCurrentLimit,
      totalPages: masterDataTotalPages,
      isLoading: isLoadingMasterData,
    } = useBpmDatasetRecords(
      isOpen && masterDataConfig?.tableCode
        ? masterDataConfig.tableCode
        : undefined,
      masterDataQueryParams,
    );

    useEffect(() => {
      const currentConfig = externalConfigRef.current;
      if (!isOpen || !currentConfig) return;

      const { url } = currentConfig.api_config;
      if (!url.trim()) {
        setExternalRows([]);
        setExternalError("");
        return;
      }

      let cancelled = false;
      setExternalError("");

      const request: ModalSelectionExternalApiOptionsRequest = {
        serialNumber: runtimeApplication?.serialNumber,
        formRevisionId:
          runtimeApplication?.formInstance.form.revisionId ??
          runtimeForm?.revisionId,
        bindingId: runtimeForm?.bindingId,
        entityKey,
        timeZone: userTimeZone,
        max_records: EXTERNAL_API_MAX_RECORDS,
        filter: hasDatasourceFilterExpression ? undefined : requestFilterParams,
        search: requestSearchParams,
        ...(hasDatasourceFilterExpression && stableFilterExpressionPayload
          ? { filterExpression: stableFilterExpressionPayload }
          : {}),
      };

      void externalApiRef
        .current(request)
        .then((response) => {
          if (cancelled) return;

          setExternalRows(normalizeRows(response.data?.items));
        })
        .catch((error) => {
          if (cancelled) return;
          setExternalRows([]);
          setExternalError(
            error instanceof Error
              ? error.message
              : "Failed to load external data.",
          );
        });

      return () => {
        cancelled = true;
      };
    }, [
      filterExpressionPayloadKey,
      hasDatasourceFilterExpression,
      isOpen,
      entityKey,
      requestFilterParams,
      requestSearchParams,
      runtimeApplication?.formInstance.form.revisionId,
      runtimeApplication?.serialNumber,
      runtimeForm?.bindingId,
      runtimeForm?.revisionId,
      stableFilterExpressionPayload,
      userTimeZone,
    ]);

    useEffect(() => {
      if (!isOpen) return;

      setSearchQuery("");
      setPage(1);

      const currentValue = isRecord(props.entity.value)
        ? (props.entity.value as PickerRow)
        : undefined;
      setSelectedRow(currentValue);
      setSelectedKey(
        currentValue ? getRowSelectionKey(currentValue) : undefined,
      );
    }, [isOpen, props.entity.value]);

    useEffect(() => {
      const nextColumn = searchOptions[0]?.value ?? "";
      setSearchColumn((current) =>
        searchOptions.some((option) => option.value === current)
          ? current
          : nextColumn,
      );
    }, [searchOptions]);

    useEffect(() => {
      setPage(1);
    }, [datasourceFiltersKey, pageSize, searchColumn, trimmedSearchQuery]);

    const sourceRows = useMemo<PickerRow[]>(
      () =>
        datasource?.type === "external-api"
          ? externalRows
          : normalizeRows(masterDataRows),
      [datasource?.type, externalRows, masterDataRows],
    );

    const filteredRows = useMemo(() => {
      if (useServerPagination) {
        return sourceRows;
      }

      const rowsAfterDatasourceFilters = applyDatasourceFilters(
        sourceRows,
        localDatasourceFilters,
      );

      if (requestSearchParams) {
        return rowsAfterDatasourceFilters;
      }

      const loweredSearch = trimmedSearchQuery.toLowerCase();
      if (!loweredSearch || !searchColumn) return rowsAfterDatasourceFilters;

      return rowsAfterDatasourceFilters.filter((row) =>
        String(resolvePath(row, searchColumn) ?? "")
          .toLowerCase()
          .includes(loweredSearch),
      );
    }, [
      localDatasourceFilters,
      requestSearchParams,
      searchColumn,
      sourceRows,
      trimmedSearchQuery,
      useServerPagination,
    ]);

    const totalItems = useServerPagination
      ? masterDataTotal
      : filteredRows.length;
    const totalPages = useServerPagination
      ? Math.max(1, masterDataTotalPages)
      : Math.max(1, Math.ceil(totalItems / pageSize));
    const safePage =
      useServerPagination && isLoadingMasterData
        ? page
        : Math.min(page, totalPages);

    useEffect(() => {
      if (page !== safePage) {
        setPage(safePage);
      }
    }, [page, safePage]);

    const paginatedRows = useMemo(() => {
      if (useServerPagination) {
        return filteredRows;
      }

      const start = (safePage - 1) * pageSize;
      return filteredRows.slice(start, start + pageSize);
    }, [filteredRows, pageSize, safePage, useServerPagination]);

    const displayedPage = useServerPagination
      ? masterDataCurrentPage
      : safePage;
    const displayedPageSize = useServerPagination
      ? masterDataCurrentLimit
      : pageSize;

    const isLoading =
      isOpen &&
      (datasource?.type === "master-data"
        ? isLoadingMasterData
        : isTestingExternalApi);

    const schemaError = formatError(props.entity.value, props.entity.error)
      ?._errors?.[0];
    const canConfirm = Boolean(selectedRow);

    const handleConfirm = () => {
      if (!selectedRow) return;

      if (interpreterStore && mappedColumns.length > 0) {
        const currentValues = (interpreterStore.getEntitiesValues?.() ??
          {}) as Record<string, unknown>;
        const nextValues = { ...currentValues };

        mappedColumns.forEach(({ sourceKey, targetFieldName }) => {
          const targetEntityId = resolveFieldIdByName(
            targetFieldName,
            interpreterStore,
          );
          if (!targetEntityId) return;

          const targetEntity = interpreterStore.schema?.entities?.[
            targetEntityId
          ] as
            | {
                type?: string;
                attributes?: Record<string, unknown>;
              }
            | undefined;
          if (!isTargetFieldEditable(targetEntity)) {
            return;
          }

          nextValues[targetEntityId] = coerceMappedValueForEntity(
            resolvePath(selectedRow, sourceKey),
            targetEntity,
          );
        });

        interpreterStore.setData({
          entitiesValues: nextValues,
          entitiesErrors: {},
        });
      }

      props.setValue(selectedRow);
      props.resetError?.();
      setIsOpen(false);
    };

    const renderTableContent = () => {
      if (isLoading) {
        return (
          <div className="flex min-h-[320px] items-center justify-center text-base text-secondary-text">
            Loading...
          </div>
        );
      }

      if (externalError) {
        return (
          <div className="flex min-h-[320px] items-center justify-center px-6 text-center text-base text-red">
            {externalError}
          </div>
        );
      }

      if (columns.length === 0) {
        return (
          <div className="flex min-h-[320px] items-center justify-center text-base text-secondary-text">
            No display columns configured.
          </div>
        );
      }

      if (filteredRows.length === 0) {
        return (
          <div className="flex min-h-[320px] items-center justify-center text-base text-secondary-text">
            No data
          </div>
        );
      }

      return (
        <>
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="min-w-full table-fixed">
              <thead className="sticky top-0 z-[1] bg-gray-2">
                <tr className="border-b border-stroke bg-gray-2">
                  <th className="h-11 w-[60px] bg-gray-2 px-5 py-2.5" />
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className="h-11 min-w-[160px] bg-gray-2 px-5 py-2.5 text-left text-base font-medium leading-6 text-dark"
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((row, rowIndex) => {
                  const rowKey = getRowSelectionKey(row);
                  const isSelected = selectedKey === rowKey;

                  return (
                    <tr
                      key={`${rowKey}-${rowIndex}`}
                      className={cn(
                        "h-14 border-b border-stroke transition-colors",
                        isSelected
                          ? "bg-[#F0F7FF]"
                          : "bg-white hover:bg-gray-1",
                      )}
                    >
                      <td className="h-14 px-5 py-2.5 align-middle">
                        <button
                          type="button"
                          className="flex h-6 w-6 items-center justify-center"
                          onClick={() => {
                            setSelectedKey(rowKey);
                            setSelectedRow(row);
                          }}
                          aria-label={`Select ${label} row ${rowIndex + 1}`}
                        >
                          <SelectionIndicator selected={isSelected} />
                        </button>
                      </td>
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className="h-14 px-5 py-2.5 align-middle"
                        >
                          <button
                            type="button"
                            className="block w-full truncate text-left text-base font-medium leading-6 text-dark"
                            onClick={() => {
                              setSelectedKey(rowKey);
                              setSelectedRow(row);
                            }}
                          >
                            {formatCellValue(resolvePath(row, column.key))}
                          </button>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <TablePagination
            currentPage={displayedPage}
            pageSize={displayedPageSize}
            totalItems={totalItems}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
          />
        </>
      );
    };

    return (
      <div className="w-full">
        <Label aria-required={props.entity.attributes.required}>{label}</Label>

        <div className="mt-1">
          <Button
            type="button"
            variant="tertiary"
            disabled={props.entity.attributes.disabled}
            aria-label={`Open ${label}`}
            onClick={() => setIsOpen(true)}
          >
            {buttonText}
          </Button>
        </div>

        {schemaError ? <ValidationError>{schemaError}</ValidationError> : null}

        <Modal
          isOpen={isOpen}
          close={() => setIsOpen(false)}
          size="full"
          className="max-w-[1240px]"
          closeOnOverlayClick={false}
        >
          <div className="flex max-h-[90dvh] min-h-0 flex-col bg-white px-[30px] pb-[30px] pt-[30px]">
            <h2 className="shrink-0 text-center text-2xl font-semibold leading-[30px] text-dark">
              {label}
            </h2>

            <div className="mt-[30px] flex min-h-0 flex-1 flex-col gap-5 overflow-hidden">
              <div className="flex shrink-0 flex-col gap-3 lg:flex-row">
                <div className="w-full lg:max-w-[220px]">
                  <Select
                    options={searchOptions}
                    value={searchColumn}
                    onChange={(value: string) => setSearchColumn(value)}
                    placeholder="Select a field"
                    disabled={searchOptions.length === 0}
                  />
                </div>
                <div className="w-full lg:max-w-[500px]">
                  <Input
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search..."
                    leftIcon={<Search className="h-5 w-5" />}
                  />
                </div>
              </div>

              <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[8px] border border-stroke bg-white">
                {renderTableContent()}
              </div>
            </div>

            <div className="mt-[30px] flex shrink-0 items-center justify-center gap-[18px]">
              <Button
                type="button"
                variant="secondary"
                className="w-[190px]"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="w-[190px]"
                onClick={handleConfirm}
                disabled={!canConfirm}
              >
                OK
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    );
  },
);
